import type { Attachment, SharedLocationResponse } from "stream-chat";
import type { ChannelEligibility } from "Types/Chat/channelEligibility";
import type { ChannelKind } from "Types/Chat/channelKind";
import { Resources, type TFunction } from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";

declare module "stream-chat" {
  interface CustomChannelData {
    name?: string;
    channelKind?: ChannelKind;
    /**
     * Who may be a member. The API writes it on every channel it creates ("anyone" on direct
     * ones, where no rule applies); absent only on channels created before the field existed.
     */
    eligibility?: ChannelEligibility;
  }

  interface CustomAttachmentData {
    transactionPublicID?: string;
    amount?: number;
    currencyCode?: string;
    transactionStatus?: string;
    variableSymbol?: string;
    recipientName?: string;
    recipientIdentityUserPublicID?: string;
    isClosed?: boolean;
  }
}

export const CUSTOM_ATTACHMENT_TYPES = [
  "payment_request",
  "payment_request_multi",
  "direct_payment",
] as const;

export type CustomAttachmentType = (typeof CUSTOM_ATTACHMENT_TYPES)[number];

export type PaymentRequestAttachment = Attachment & {
  type: "payment_request";
  transactionPublicID: string;
  amount: number;
  currencyCode: string;
  transactionStatus: string;
};

/**
 * A multi-payer payment request (group channels): the author asks every member for a FIXED
 * amount and any number of them may pay it, each payment being its own transaction created at
 * pay time (a direct payment to the author, posted as a thread reply under this message). Unlike
 * `payment_request` there is NO transaction behind the request itself — the attachment is the
 * whole record, and `isClosed` is the author's own flag that hides the Pay button (a courtesy,
 * not server-enforced). The amount always lands on the author's primary account in `currencyCode`.
 */
export type MultiPayerPaymentRequestAttachment = Attachment & {
  type: "payment_request_multi";
  recipientIdentityUserPublicID: string;
  amount: number;
  currencyCode: string;
  isClosed?: boolean;
};

export type DirectPaymentAttachment = Attachment & {
  type: "direct_payment";
  transactionPublicID: string;
  amount: number;
  currencyCode: string;
  transactionStatus: string;
};

const isAttachment = (
  a: Attachment | SharedLocationResponse,
): a is Attachment => "type" in a;

export const isCustomAttachment = (
  a: Attachment | SharedLocationResponse,
): boolean =>
  isAttachment(a) &&
  a.type !== undefined &&
  CUSTOM_ATTACHMENT_TYPES.includes(a.type as CustomAttachmentType);

/**
 * The `text` a payment message is sent with. Stream renders the push banner body from
 * `message.text` and its v2 payload carries no attachments, so without this the recipient's
 * banner has nothing to say about the payment. Fixed at send time in the SENDER's language;
 * the bubble hides it (ChatMessageWithDisclosedName) because the card already shows the amount,
 * and the channel list builds its own preview (ChatChannelPreview).
 *
 *   payment_request       → "Payment request · 100 Kč"
 *   payment_request_multi → "Payment request · 100 Kč"
 *   direct_payment        → "Sent 100 Kč"    (the banner title is already the sender's name)
 */
export const buildPaymentMessageText = (
  t: TFunction,
  {
    amount,
    currencyCode,
    type,
  }: {
    type: CustomAttachmentType;
    amount: number;
    currencyCode: string;
  },
): string => {
  const money = formatCurrency({ amount, currencyCode });

  return type === "direct_payment"
    ? `${t(Resources.Chat.PushCaption.DirectPaymentSent)} ${money}`
    : `${t(Resources.Chat.PushCaption.PaymentRequest)} · ${money}`;
};
